import DataIsReprocessingBar from 'components/ReusableComponents/DataIsReprocessingBar'
import CustomHeader from 'components/ReusableComponents/HeaderTitles'
import BulbIcon from 'components/ReusableComponents/Icons/BulbIcon'
import LoadingIndicator from 'components/ReusableComponents/LoadingIndicator'
import { useEngagePlusGate } from 'components/ReusableComponents/UnlockServiceDialog'
import ChatBotBottomBar from 'features/ChatBotSettings/BottomBar'
import BreakfastIcon from 'features/ChatBotSettings/BreakfastIcon'
import EarlyCheckinIcon from 'features/ChatBotSettings/EarlyCheckin'
import FitnessCenterHoursIcon from 'features/ChatBotSettings/FitnessCenterHours'
import ParkingIcon from 'features/ChatBotSettings/Parking'
import PoolHoursIcon from 'features/ChatBotSettings/PoolHours'
import ResortFeeInclusionIcon from 'features/ChatBotSettings/ResortFeeInclusion'
import RestaurantTimeIcon from 'features/ChatBotSettings/RestautrantIcon'
import TransportationIcon from 'features/ChatBotSettings/Transportation'
import ValetIcon from 'features/ChatBotSettings/ValetIcon'
import WifiIcon from 'features/ChatBotSettings/WifiIcon'
import useCopyText from 'hooks/useCopyText'
import usePlausible from 'hooks/usePlausible'
import { useEffect, useState } from 'react'
import { useToasts } from 'react-toast-notifications'
import ReactTooltip from 'react-tooltip'
import subsidiariesApi from 'services/api/subsidiaries'

import styles from './ChatBotSettings.module.scss'
import LateCheckoutIcon from './LateCheckoutIcon'
import LostAndFoundIcon from './LostAndFoundIcon'
import ReceiptRequestIcon from './ReceiptRequest'

const queryNames = {
  Breakfast: 'Breakfast',
  Wifi: 'Wifi',
  ResortFeeInclusion: 'Resort fee inclusion',
  RestaurantTime: 'Restaurant time',
  PoolHours: 'Pool hours',
  FitnessCenterHours: 'Fitness center hours',
  Transportation: 'Transportation',
  Parking: 'Parking',
  EarlyCheckIn: 'Early check-in',
  Valet: 'Valet (ACE parking)',
  ReceiptRequest: 'Receipt Request',
  LateCheckout: 'Late checkout',
  LostAndFound: 'Lost and found'
}

const ResponseIcon = ({ kind }) => {
  switch (kind) {
    case 'Wifi':
      return <WifiIcon />
    case 'Breakfast':
      return <BreakfastIcon />
    case 'ResortFeeInclusion':
      return <ResortFeeInclusionIcon />
    case 'RestaurantTime':
      return <RestaurantTimeIcon />
    case 'PoolHours':
      return <PoolHoursIcon />
    case 'FitnessCenterHours':
      return <FitnessCenterHoursIcon />
    case 'Transportation':
      return <TransportationIcon />
    case 'Parking':
      return <ParkingIcon />
    case 'EarlyCheckIn':
      return <EarlyCheckinIcon />
    case 'Valet':
      return <ValetIcon />
    case 'ReceiptRequest':
      return <ReceiptRequestIcon />
    case 'LateCheckout':
      return <LateCheckoutIcon />
    case 'LostAndFound':
      return <LostAndFoundIcon />

    default:
      return null
  }
}

const Bubble = ({ children }) => {
  const copy = useCopyText()

  return (
    <div className={styles.bubbleHolder} onClick={() => copy(children, '')}>
      {/* <GuestIcon className={styles.bubbleGuestIcon} /> */}
      <div className={styles.bubble} data-tip="Click to copy">
        <div className={styles.bubbleCorner}></div>
        <div className={styles.bubbleContent}>{children}</div>
      </div>
    </div>
  )
}

const exampleResponses = {
  Breakfast: [
    'Breakfast is served from 7am-11am. Its $14.46 and is a continental style breakfast.',
    'We do offer a full service restaurant on-site. It is open Monday through Friday from 8-11am. We offer a full menu and also offer breakfast vouchers for purchase at our Front Desk that allow you to enjoy a breakfast entree and a beverage for just $9.99 per voucher. '
  ],
  Wifi: [
    'Free Wi-Fi is available in the lobby and the rooms. Use password 12345 to connect.',
    'We offer free Wi-Fi in the lobby. Use your room number and password 12345 to connect.',
    'To connect to our free Wi-Fi, use your room number and password "Welcome". '
  ],
  ResortFeeInclusion: [
    "The resort fee is mandatory and includes access to our fitness center, pool, and other amenities. The daily charge for the resort fee is $30 per room. The cancellation policy for the resort fee is in line with our hotel's overall cancellation policy.",
    'There is no resort fee associated with your stay at our hotel. We want you to enjoy all of our amenities without any additional fees or charges.'
  ],
  RestaurantTime: [
    'Our restaurant is open for breakfast from 6:00 AM to 10:00 AM and for dinner from 5:00 PM to 9:00 PM. Please note that these hours may vary based on seasonal demand. We recommend making a reservation in advance to ensure availability.',
    "We don't have an on-site restaurant, but we're happy to recommend nearby dining options and help with reservations."
  ],
  PoolHours: [
    'Our pool is open from 8:00 AM to 10:00 PM daily, and we provide complimentary towels and lounge chairs for our guests. Please note that these hours may vary based on seasonal demand and maintenance needs.',
    "Sorry, we don't have a pool. We can suggest local attractions and activities for you to enjoy instead."
  ],
  FitnessCenterHours: [
    'Our fitness center is open 24 hours a day for our guests, and we offer a range of equipment and amenities to support your workout routine. Please note that access to the fitness center is included in our resort fee, which is $30 per room per day.',
    'Fitness center is open from 8:00 AM to 9:00 PM and includes [list of equipment]. Access to the fitness center is included in our resort fee'
  ],
  Transportation: [
    'We offer free shuttle to/from airport from 6:00 AM to 11:00 PM daily. Paid shuttle to nearby attractions/restaurants at $20/person/trip. Concierge can help with taxi/car rental. Nearby public transportation includes bus stop 2 blocks away.',
    "We don't offer transportation services, but we're happy to assist with arranging taxi or car rental services through our concierge."
  ],
  Parking: [
    'We offer free parking for our guests. Please note that parking is limited and available on a first-come, first-served basis.',
    'We offer valet parking for $20 per day. Please note that parking is limited and available on a first-come, first-served basis.'
  ],
  EarlyCheckIn: [
    'Early check-in is available for an additional fee of $50. Please note that early check-in is subject to availability and cannot be guaranteed.',
    'There is an additional $55 for early check in at 12pm. Also it is not guaranteed I recommend you call in the morning around 11am to double check.'
  ],
  Valet: [
    'Text "Car" to 555-555-5555 to request your car. Please allow 15 minutes for your car to be ready.'
  ],
  ReceiptRequest: [
    'Please text our Front Desk Team at 555-555-5555 for to request your folio.',
    'To receive your receipt, please send an email to xxx, including your name, your booking reference number and phone number.'
  ],
  LateCheckout: [
    'Please text our Front Desk Team at 555-555-5555 for late checkout availability.',
    'Unfortunately, in case of late check-outs, you will be responsible for the cost of the room for that night.'
  ],
  LostAndFound: [
    'Please text 555-555-5555 to report your lost item.',
    'Please call our front desk on 555-555-5555 for lost and found queries.',
    'Please fill out the form on our Lost and Found page with the details of the item you have misplaced, and our team will check for it. [add link here]'
  ]
}

const exampleQueries = {
  Breakfast: [
    'What time is breakfast served?',
    'Do you have a restaurant on site?',
    `Is breakfast included in the price of our stay or is it extra?`
  ],
  Wifi: [
    'Do you have free wifi?',
    `How do I access the wifi? I don't remember setting up a username or password`,
    'What is the wifi password?'
  ],
  ResortFeeInclusion: [
    'Is the amenities fee mandatory?',
    'How much is the daily charge for resort amenities?',
    "What's the cancellation policy for the amenities fee?"
  ],
  RestaurantTime: [
    'Is there a dress code for the restaurant?',
    'What time does the breakfast service start?',
    'Are there any vegetarian or gluten-free options available at the restaurant?'
  ],
  PoolHours: [
    'When does the pool open and close?',
    'Is the pool heated?',
    'Are there any lifeguards on duty?'
  ],
  FitnessCenterHours: [
    'What are the gym hours?',
    'Is there a yoga class this afternoon?',
    'Are there any personal trainers available?'
  ],
  Transportation: [
    'Is there a shuttle service to the airport?',
    'How much does a taxi ride to downtown cost?',
    "What's the nearest train station?",
    'Are there any public transit options nearby?'
  ],
  Parking: [
    'Is there parking available?',
    'How much does parking cost?',
    'Is there a parking garage or is it street parking?'
  ],
  EarlyCheckIn: ['Can I check in early?', 'Is early check-in available?'],
  Valet: ['Car'],
  ReceiptRequest: [
    'Can you send me an itemized bill during stay here?',
    'Hi, May I have receipt from my stay forwarded to my email?',
    'Hi can you please send me the receipt for Mar 2'
  ],
  LateCheckout: [
    'Hi can I request for 4pm checkout?',
    'Do you have a late checkout available?'
  ],
  LostAndFound: [
    'I left an item in the room I stayed at and wondering if you could check on it. Thank you!',
    'I lost my license did anyone turn it in at the hotel?',
    'hi I lost a computer mouse at room 313'
  ]
}

const hints = {
  Wifi: "Make sure your reply includes instructions to connect Wi-Fi, where it is available and if it's free or paid.",
  Breakfast:
    "If breakfast is available, specify the price, when it's being served and the type of breakfast.",
  ResortFeeInclusion:
    'Explain mandatory charge, list inclusive amenities/services, share daily room cost.',
  RestaurantTime:
    'Please specify restaurant hours for each meal, seasonal variations, suggest advance reservation, and welcome further inquiries/requests.',
  PoolHours:
    'Share pool hours, towel/chair availability, seasonal/maintenance changes impacting availability',
  FitnessCenterHours:
    'Specify fitness center hours, available amenities/equipment, and if resort fee covers access.',
  Transportation:
    'List services available, such as shuttle (free/paid) and help with taxis/car rentals. Also mention nearby public transportation options.',
  Parking:
    'Specify if parking is available, if it is free or paid, and if it is limited.',
  EarlyCheckIn:
    'Specify if early check-in is available, early check-in time, if it is free or paid, and if it is limited.',
  Valet: `This response is useful if your property is using ACE parking. Redirect guests to ACE parking phone number when they text your hotel.`
}

const ResponseCard = ({ response, onChange }) => {
  const [activeTab, setActiveTab] = useState('responses')

  return (
    <div className={styles.cardHolder}>
      <div className={styles.card}>
        <div className={styles.cardInner}>
          <div className={styles.cardContent}>
            <div className={styles.cardTitle}>
              <ResponseIcon kind={response.kind} />
              {queryNames[response.kind]} response
            </div>
            <p className={styles.textAreaLabel}>Automated message</p>
            <textarea
              className={styles.textArea}
              value={response.content}
              onChange={e => onChange(e.target.value)}
              placeholder="Type your response here"
            />
            {hints[response.kind] && (
              <div className={styles.hint}>
                <BulbIcon className={styles.hintIcon} />
                <p className={styles.hintText}>{hints[response.kind]}</p>
              </div>
            )}
          </div>
          <div className={styles.examples}>
            <div className={styles.exampleTitles}>
              <p
                className={
                  activeTab === 'responses'
                    ? styles.examplesTitleActive
                    : styles.examplesTitle
                }
                onClick={() => setActiveTab('responses')}
              >
                Responses we love
              </p>
              <p
                className={
                  activeTab === 'queries'
                    ? styles.examplesTitleActive
                    : styles.examplesTitle
                }
                onClick={() => setActiveTab('queries')}
              >
                Guest queries
              </p>
            </div>
            {activeTab === 'responses' && (
              <div className={styles.exampleTab}>
                {exampleResponses[response.kind].map((example, i) => (
                  <Bubble key={i}>{example}</Bubble>
                ))}
              </div>
            )}
            {activeTab === 'queries' && (
              <div className={styles.exampleTab}>
                {exampleQueries[response.kind].map((example, i) => (
                  <Bubble key={i}>{example}</Bubble>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

const ChatBotSettings = () => {
  const { addToast } = useToasts()
  const { promptEngagePlusUpgrade, promptEngagePlusUpgradeOnEntry } = useEngagePlusGate()

  const [isLoading, setIsLoading] = useState(true)
  const [isErred, setIsErred] = useState(false)
  const [responses, setResponses] = useState([])

  useEffect(() => {
    subsidiariesApi.autoresponses
      .get()
      .then(res => {
        setResponses(res)
      })
      .catch(() => {
        setIsErred(true)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [])

  useEffect(() => {
    promptEngagePlusUpgradeOnEntry()
  }, [promptEngagePlusUpgradeOnEntry])

  const { postEvent, plausibleEvents } = usePlausible()

  function saveResponses() {
    if (promptEngagePlusUpgrade()) {
      return
    }

    postEvent(plausibleEvents.UpdateChatbotResponses)

    setIsLoading(true)
    setIsErred(false)
    subsidiariesApi.autoresponses
      .update(responses.map(r => ({ ...r, content: r.content.trim() })))
      .then(() => {
        addToast('Chatbot responses updated successfully', { appearance: 'success' })
        setIsLoading(false)
      })
      .catch(() => {
        addToast('Failed to update chatbot responses', { appearance: 'error' })
        setIsErred(true)
        setIsLoading(false)
      })
  }

  function handleResponseChange(kind, value) {
    setResponses(prevResponses => {
      const newResponses = prevResponses.map(response => {
        if (response.kind === kind) {
          return { ...response, content: value }
        }
        return response
      })
      return newResponses
    })
  }

  if (isLoading) {
    return (
      <div className={styles.wrapper}>
        <CustomHeader
          title="Chatbot responses"
          width={900}
          subtitle="Add automated replies to guest queries"
        />
        <LoadingIndicator />
      </div>
    )
  }

  if (isErred) {
    return (
      <div className={styles.wrapper}>
        <CustomHeader
          title="Chatbot responses"
          subtitle="Add automated replies to guest queries"
          width={900}
        />
        <div className={styles.error}>Something went wrong</div>
      </div>
    )
  }

  return (
    <div className={styles.wrapper}>
      <ReactTooltip />
      <CustomHeader
        title="Chatbot responses"
        subtitle="Add automated replies to guest queries"
        width={900}
      />
      <DataIsReprocessingBar marginBottom={20} marginTop={0} />
      {responses.map(response => (
        <ResponseCard
          key={response.kind}
          response={response}
          onChange={value => handleResponseChange(response.kind, value)}
        />
      ))}
      <ChatBotBottomBar onSubmit={saveResponses} />
    </div>
  )
}

export default ChatBotSettings
